'use client';

import { useMemo, useState, use } from 'react';
import { globalAccounts } from '@/lib/data/global-accounts';
import { Account } from '@/lib/data/types';
import { SchoolUnit, validUnits } from '@/lib/data';
import {
    FileStack,
    Search,
    Filter,
    Plus,
    Download,
    ChevronDown,
    ChevronRight,
    X,
    Calendar,
    Info,
    Eye,
    Edit2,
    Power,
    TrendingUp,
    TrendingDown,
    Scale,
    Wallet,
    BarChart3
} from 'lucide-react';
import Link from 'next/link';
import { notFound, useRouter } from 'next/navigation';

export default function UnitAccountsPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit } = use(params);
    const router = useRouter();

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState<string>('All');
    const [filterStatus, setFilterStatus] = useState<string>('All');
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

    // Toggle expand/collapse parent
    const toggleParent = (parentId: string) => {
        setExpandedParents(prev => {
            const next = new Set(prev);
            if (next.has(parentId)) {
                next.delete(parentId);
            } else {
                next.add(parentId);
            }
            return next;
        });
    };

    // Expand all parents by default
    useMemo(() => {
        const parents = globalAccounts.filter(acc => !acc.parentId).map(acc => acc.id);
        setExpandedParents(new Set(parents));
    }, []);

    // Derived Data
    const filteredAccounts = useMemo(() => {
        return globalAccounts.filter(a => {
            const matchesSearch = a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                a.code.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = filterCategory === 'All' || a.type === filterCategory;
            const matchesStatus = filterStatus === 'All' || a.status === filterStatus;
            return matchesSearch && matchesCategory && matchesStatus;
        }).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    }, [searchTerm, filterCategory, filterStatus]);

    // Group accounts for display (parent + children)
    const displayAccounts = useMemo(() => {
        const result: (Account & { isVisible: boolean })[] = [];

        filteredAccounts.forEach(account => {
            if (!account.parentId) {
                // Parent account
                result.push({ ...account, isVisible: true });

                // Add children if parent is expanded
                if (expandedParents.has(account.id)) {
                    const children = filteredAccounts.filter(a => a.parentId === account.id);
                    children.forEach(child => {
                        result.push({ ...child, isVisible: true });
                    });
                }
            }
        });

        return result;
    }, [filteredAccounts, expandedParents]);

    const stats = useMemo(() => {
        const typeCounts = globalAccounts.reduce((acc: any, curr) => {
            acc[curr.type] = (acc[curr.type] || 0) + 1;
            return acc;
        }, {});
        return {
            total: globalAccounts.length,
            assets: typeCounts.asset || 0,
            liabilities: typeCounts.liability || 0,
            equity: typeCounts.equity || 0,
            income: typeCounts.income || 0,
            expense: typeCounts.expense || 0,
        };
    }, []);

    const getCategoryLabel = (type: string) => {
        switch (type) {
            case 'asset': return 'Aset';
            case 'liability': return 'Liabilitas';
            case 'equity': return 'Ekuitas';
            case 'income': return 'Pendapatan';
            case 'expense': return 'Beban';
            default: return type;
        }
    };

    const getCategoryIcon = (type: string) => {
        switch (type) {
            case 'asset': return TrendingUp;
            case 'liability': return TrendingDown;
            case 'equity': return Scale;
            case 'income': return Wallet;
            case 'expense': return BarChart3;
            default: return FileStack;
        }
    };

    const handleEditAccount = (account: Account) => {
        router.push(`/yayasan/${unit}/keuangan/akun/${account.id}`);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-black text-foreground tracking-tight">
                            Chart of Accounts
                        </h1>
                        <p className="text-sm text-muted font-semibold mt-1">
                            Master Data Akun Keuangan Yayasan
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-background border border-border-main text-secondary text-xs font-bold uppercase tracking-widest hover:border-primary hover:text-primary transition-all shadow-sm">
                            <Download className="w-4 h-4" /> Export
                        </button>
                        <Link href={`/yayasan/${unit}/keuangan/akun/tambah`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-white text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-all shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4" /> Tambah Akun
                        </Link>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="bg-surface border border-border-main rounded-2xl p-5 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <FileStack className="w-5 h-5 text-muted" />
                            <span className="text-2xl font-black text-foreground">{stats.total}</span>
                        </div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Akun</p>
                    </div>
                    <div className="bg-success-soft/20 border border-success/10 rounded-2xl p-5 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <TrendingUp className="w-5 h-5 text-success" />
                            <span className="text-2xl font-black text-success">{stats.assets}</span>
                        </div>
                        <p className="text-[10px] font-bold text-success uppercase tracking-widest">Aset</p>
                    </div>
                    <div className="bg-error-soft/20 border border-error/10 rounded-2xl p-5 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <TrendingDown className="w-5 h-5 text-error" />
                            <span className="text-2xl font-black text-error">{stats.liabilities}</span>
                        </div>
                        <p className="text-[10px] font-bold text-error uppercase tracking-widest">Liabilitas</p>
                    </div>
                    <div className="bg-primary-soft/20 border border-primary/10 rounded-2xl p-5 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <Scale className="w-5 h-5 text-primary" />
                            <span className="text-2xl font-black text-primary">{stats.equity}</span>
                        </div>
                        <p className="text-[10px] font-bold text-primary uppercase tracking-widest">Ekuitas</p>
                    </div>
                    <div className="bg-info-soft/20 border border-info/10 rounded-2xl p-5 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <Wallet className="w-5 h-5 text-info" />
                            <span className="text-2xl font-black text-info">{stats.income}</span>
                        </div>
                        <p className="text-[10px] font-bold text-info uppercase tracking-widest">Pendapatan</p>
                    </div>
                    <div className="bg-warning-soft/20 border border-warning/10 rounded-2xl p-5 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <BarChart3 className="w-5 h-5 text-warning" />
                            <span className="text-2xl font-black text-warning">{stats.expense}</span>
                        </div>
                        <p className="text-[10px] font-bold text-warning uppercase tracking-widest">Beban</p>
                    </div>
                </div>
            </div>

            {/* Filters & Search */}
            <div className="bg-surface border border-border-main rounded-2xl p-6 mb-6 shadow-sm">
                <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari kode atau nama akun..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold text-foreground placeholder:text-muted focus:outline-none focus:border-primary transition-colors"
                        />
                    </div>

                    {/* Category Filter */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setShowCategoryDropdown(!showCategoryDropdown);
                                setShowStatusDropdown(false);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-background border border-border-main text-sm font-bold text-secondary hover:border-primary transition-all min-w-[180px] justify-between w-full md:w-auto"
                        >
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4" />
                                <span>{filterCategory === 'All' ? 'Semua Kategori' : getCategoryLabel(filterCategory)}</span>
                            </div>
                            <ChevronDown className="w-4 h-4" />
                        </button>
                        {showCategoryDropdown && (
                            <div className="absolute top-full mt-2 w-full bg-surface border border-border-main rounded-xl shadow-xl z-50 overflow-hidden">
                                <button
                                    onClick={() => { setFilterCategory('All'); setShowCategoryDropdown(false); }}
                                    className="w-full px-4 py-3 text-left text-sm font-semibold hover:bg-primary-soft/10 transition-colors"
                                >
                                    Semua Kategori
                                </button>
                                {['asset', 'liability', 'equity', 'income', 'expense'].map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => { setFilterCategory(cat); setShowCategoryDropdown(false); }}
                                        className="w-full px-4 py-3 text-left text-sm font-semibold hover:bg-primary-soft/10 transition-colors"
                                    >
                                        {getCategoryLabel(cat)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Status Filter */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setShowStatusDropdown(!showStatusDropdown);
                                setShowCategoryDropdown(false);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-background border border-border-main text-sm font-bold text-secondary hover:border-primary transition-all min-w-[180px] justify-between w-full md:w-auto"
                        >
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4" />
                                <span>{filterStatus === 'All' ? 'Semua Status' : filterStatus === 'active' ? 'Aktif' : 'Nonaktif'}</span>
                            </div>
                            <ChevronDown className="w-4 h-4" />
                        </button>
                        {showStatusDropdown && (
                            <div className="absolute top-full mt-2 w-full bg-surface border border-border-main rounded-xl shadow-xl z-50 overflow-hidden">
                                <button
                                    onClick={() => { setFilterStatus('All'); setShowStatusDropdown(false); }}
                                    className="w-full px-4 py-3 text-left text-sm font-semibold hover:bg-primary-soft/10 transition-colors"
                                >
                                    Semua Status
                                </button>
                                <button
                                    onClick={() => { setFilterStatus('active'); setShowStatusDropdown(false); }}
                                    className="w-full px-4 py-3 text-left text-sm font-semibold hover:bg-primary-soft/10 transition-colors"
                                >
                                    Aktif
                                </button>
                                <button
                                    onClick={() => { setFilterStatus('inactive'); setShowStatusDropdown(false); }}
                                    className="w-full px-4 py-3 text-left text-sm font-semibold hover:bg-primary-soft/10 transition-colors"
                                >
                                    Nonaktif
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Accounts Table - Hierarchical with Expand/Collapse */}
            <div className="bg-surface border border-border-main rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-4 text-[11px] font-bold text-muted uppercase tracking-widest w-40">Account No</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-muted uppercase tracking-widest">Name</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-muted uppercase tracking-widest text-center w-48">Type</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-muted uppercase tracking-widest text-right w-56">Balance</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-muted uppercase tracking-widest text-center w-32">Status</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-muted uppercase tracking-widest text-right w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/30">
                            {displayAccounts.length > 0 ? displayAccounts.map((account) => {
                                const isParent = !account.parentId;
                                const isChild = !!account.parentId;
                                const isExpanded = expandedParents.has(account.id);
                                const hasChildren = globalAccounts.some(a => a.parentId === account.id);

                                return (
                                    <tr
                                        key={account.id}
                                        className={`hover:bg-primary-soft/5 transition-colors group ${isParent ? 'bg-background/20' : ''
                                            }`}
                                    >
                                        <td className="px-6 py-3.5">
                                            <span className={`font-bold tabular-nums tracking-wide ${isParent ? 'text-foreground text-sm' : 'text-muted text-xs'
                                                }`}>
                                                {account.code}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <div className={`flex items-center gap-2 ${isChild ? 'pl-8' : ''}`}>
                                                {isParent && hasChildren && (
                                                    <button
                                                        onClick={() => toggleParent(account.id)}
                                                        className="p-1 hover:bg-primary-soft/20 rounded transition-colors"
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronDown className="w-4 h-4 text-primary" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4 text-muted" />
                                                        )}
                                                    </button>
                                                )}
                                                <span className={`${isParent
                                                    ? 'font-black text-foreground text-sm'
                                                    : 'font-medium text-secondary text-sm group-hover:text-foreground transition-colors'
                                                    }`}>
                                                    {account.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3.5 text-center">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted/70">
                                                {account.type === 'asset' ? 'ASSETS' :
                                                    account.type === 'liability' ? 'LIABILITIES' :
                                                        account.type === 'equity' ? 'EQUITY' :
                                                            account.type === 'income' ? 'INCOME' :
                                                                account.type === 'expense' ? 'EXPENSES' : account.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3.5 text-right">
                                            <span className={`font-mono font-semibold tracking-tight ${account.balance === 'Rp 0' ? 'text-muted/40' : 'text-foreground'
                                                }`}>
                                                {account.balance}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3.5 text-center">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${account.status === 'active'
                                                ? 'bg-success-soft text-success'
                                                : 'bg-muted/20 text-muted'
                                                }`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${account.status === 'active' ? 'bg-success animate-pulse' : 'bg-muted'}`}></div>
                                                {account.status === 'active' ? 'Aktif' : 'Nonaktif'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3.5 text-right">
                                            <div className="flex items-center justify-end gap-2 transition-all">
                                                <button
                                                    onClick={() => setSelectedAccount(account)}
                                                    className="p-2 rounded-lg bg-background border border-border-main text-muted hover:text-primary hover:border-primary transition-all"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditAccount(account)}
                                                    className="p-2 rounded-lg bg-background border border-border-main text-muted hover:text-info hover:border-info transition-all"
                                                    title="Edit Account"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    className="p-2 rounded-lg bg-background border border-border-main text-muted hover:text-error hover:border-error transition-all"
                                                    title="Deactivate"
                                                >
                                                    <Power className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan={6} className="px-8 py-32 text-center text-muted">
                                        <div className="flex flex-col items-center gap-4 opacity-30">
                                            <FileStack className="w-16 h-16" />
                                            <p className="font-bold text-sm uppercase tracking-widest">Tidak ada akun ditemukan</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Info */}
                <div className="p-6 border-t border-border-main flex items-center justify-between bg-background/20">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">
                        Menampilkan {displayAccounts.length} dari {globalAccounts.length} akun
                    </p>
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">
                        Master Data Global Yayasan
                    </p>
                </div>
            </div>

            {/* Account Detail Modal */}
            {selectedAccount && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-8">
                    <div className="bg-surface border border-border-main rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-border-main flex items-center justify-between sticky top-0 bg-surface z-10">
                            <div>
                                <h2 className="text-xl font-black text-foreground">Detail Akun</h2>
                                <p className="text-xs text-muted font-semibold mt-1">Informasi lengkap akun keuangan</p>
                            </div>
                            <button
                                onClick={() => setSelectedAccount(null)}
                                className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-error hover:border-error transition-all"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-6">
                            {/* Account Code & Name */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Kode Akun</label>
                                    <div className="px-4 py-3 bg-background border border-border-main rounded-xl">
                                        <p className="font-bold text-primary text-lg tabular-nums">{selectedAccount.code}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Status</label>
                                    <div className="px-4 py-3 bg-background border border-border-main rounded-xl">
                                        <span className={`inline-flex items-center gap-2 text-sm font-bold ${selectedAccount.status === 'active' ? 'text-success' : 'text-muted'
                                            }`}>
                                            <div className={`w-2 h-2 rounded-full ${selectedAccount.status === 'active' ? 'bg-success animate-pulse' : 'bg-muted'}`}></div>
                                            {selectedAccount.status === 'active' ? 'Aktif' : 'Nonaktif'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Nama Akun</label>
                                <div className="px-4 py-3 bg-background border border-border-main rounded-xl">
                                    <p className="font-bold text-foreground">{selectedAccount.name}</p>
                                </div>
                            </div>

                            {/* Category & Sub-Category */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Kategori</label>
                                    <div className="px-4 py-3 bg-background border border-border-main rounded-xl">
                                        <span className="text-sm font-bold text-foreground">
                                            {getCategoryLabel(selectedAccount.type)}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Sub Kategori</label>
                                    <div className="px-4 py-3 bg-background border border-border-main rounded-xl">
                                        <p className="font-semibold text-secondary">{selectedAccount.subCategory || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Balance Type & Balance */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Tipe Saldo</label>
                                    <div className="px-4 py-3 bg-background border border-border-main rounded-xl">
                                        <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${selectedAccount.balanceType === 'debit'
                                            ? 'bg-success-soft text-success'
                                            : 'bg-error-soft text-error'
                                            }`}>
                                            {selectedAccount.balanceType}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Saldo Berjalan</label>
                                    <div className="px-4 py-3 bg-background border border-border-main rounded-xl">
                                        <p className="font-black text-foreground text-lg tabular-nums">{selectedAccount.balance}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Timestamps */}
                            {selectedAccount.createdAt && (
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Dibuat Pada</label>
                                        <div className="px-4 py-3 bg-background border border-border-main rounded-xl flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-muted" />
                                            <p className="font-semibold text-secondary text-sm">{selectedAccount.createdAt}</p>
                                        </div>
                                    </div>
                                    {selectedAccount.updatedAt && (
                                        <div>
                                            <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block">Terakhir Diubah</label>
                                            <div className="px-4 py-3 bg-background border border-border-main rounded-xl flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-muted" />
                                                <p className="font-semibold text-secondary text-sm">{selectedAccount.updatedAt}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Info Note */}
                            <div className="p-4 bg-info-soft/20 border border-info/10 rounded-xl flex items-start gap-3">
                                <Info className="w-5 h-5 text-info shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-xs font-bold text-info mb-1">Catatan Penting</p>
                                    <p className="text-xs text-secondary leading-relaxed">
                                        Saldo akun ini bersifat <strong>read-only</strong> dan dihitung otomatis dari seluruh transaksi keuangan.
                                        Akun tidak dapat dihapus jika sudah memiliki transaksi terkait.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-border-main flex items-center justify-end gap-3 bg-background/50">
                            <button
                                onClick={() => setSelectedAccount(null)}
                                className="px-6 py-2.5 rounded-xl bg-background border border-border-main text-secondary text-xs font-bold uppercase tracking-widest hover:border-primary hover:text-primary transition-all"
                            >
                                Tutup
                            </button>
                            <button
                                onClick={() => handleEditAccount(selectedAccount)}
                                className="px-6 py-2.5 rounded-xl bg-primary text-white text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                            >
                                Edit Akun
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </main>
    );
}
